/* eslint-disable max-classes-per-file */
/**
 * Enumerates supported dynamic expression categories.
 */
export enum ExpressionCategory {
    /**
     * A pipeline or a dataset parameter.
     */
    Parameter,

    /**
     * A factory level parameter.
     */
    GlobalParameter,

    /**
     * The output of a previous activity in a pipeline.
     */
    ActivityOutput,

    /**
     * A function that can be called to manipulate arguments.
     */
    Function,

    /**
    * A Fabric workspace variable.
    */
    LibraryVariable,

    /**
     * A system variable.
     */
    SystemVariable,

    /**
     * A trigger parameter.
     */
    TriggerParameter,

    /**
     * A iterable activity item.
     */
    IterableItem,

    /**
     * A string interpolation expression @{}
     */
    Interpolation,

    /**
     * A dataset/transformation column.
     */
    Column,

    /**
     * A property of another expression. E.g. @json('{ "name": "John" }').name
     */
    Property,

    /**
     * A property of a pipeline. E.g. @variables('foobar')
     */
    Variable,

    /**
     * A string containing no expression
     */
    None
}

/**
 * Splits expression functions into their own categories.
 */
export type ExpressionFunctionCategory = 'Math' | 'String' | 'Logical' | 'Date' | 'Collection' | 'Conversion' | 'Other';

/**
 * Generates a tuple with the specified arguments. This is used to generate the expression type
 * tuple
 */
const tuple = <T extends string[]>(...args: T) => args;

/**
 * Tuple containing the expression types that will be used to define the ExpressionType union type
 * as well as the expressionTypeArray in order to test if a string is a valid expression type at
 * run time. This is a workaround to avoid duplicating expression types for both ExpressionType and
 * expressionTypeArray.
 */
const expressionTypeTuple = tuple('String', 'SecureString', 'Int', 'Float', 'Bool', 'Object', 'Array', 'Any');

/**
 * Supported expression types.
 */
export type ExpressionType = (typeof expressionTypeTuple)[number];

/**
 * ExpressionType cannot be accessed at runtime, which means we cannot test whether a string literal is a
 * valid ExpressionType. This array allows checking if a string is a valid ExpressionType. In the future,
 * this could be refactored into an enum rather than a string literal union.
 */
export const expressionTypeArray = expressionTypeTuple as string[];

/**
 * Represents a system function argument.
 */
export class Argument {
    /**
     * Initializes a new instance of the Argument class.
     * @param _name The name of the argument.
     * @param _description A description of what the argument is.
     * @param _type The argument type (string, int,...)
     * @param _required Whether the argument is required or not.
     * @param _subType The argument sub type in case it was an array.
     * @param _variableLength true if the argument accepts a variable list of parameters (1..n).
     */
    constructor(
        private readonly _name: string,
        private readonly _description: string,
        private _type: ExpressionType = 'String',
        private _required: boolean = true,
        private _subType?: ExpressionType,
        private _variableLength: boolean = false) {
    }

    /**
     * Gets the argument's name.
     */
    public get name(): string {
        return this._name;
    }

    /**
     * Gets the argument's type.
     */
    // eslint-disable-next-line
    public get type(): ExpressionType {
        return this._type;
    }

    /**
     * Gets the argument's sub type (in case of arrays).
     */
    public get subType(): ExpressionType {
        return this._subType;
    }

    /**
     * Gets the argument's description.
     */
    public get description(): string {
        return this._description;
    }

    /**
     * Gets whether the argument is required or not.
     */
    public get required(): boolean {
        return this._required;
    }

    /**
     * Gets whether the argument accepts a variable list of parameters (1..n).
     */
    public get variableLength(): boolean {
        return this._variableLength;
    }

    /**
     * Sets the argument as a variable length argument (accepts one or more parameters).
     */
    public ofVariableLength(): Argument {
        this._variableLength = true;
        return this;
    }

    /**
     * Sets the argument as optional.
     */
    public optional(): Argument {
        this._required = false;
        return this;
    }

    /**
     * Indicates that the argument is a strongly typed array.
     * @param subType The array element type.
     */
    public arrayOf(subType: ExpressionType): Argument {
        this._type = 'Array';
        this._subType = subType;
        return this;
    }
}

/**
 * Represents a expression which can be used to provide dynamic runtime values to system entities such as activities, pipelines and datasets.
 */
export abstract class Expression {
    /**
     * Initializes a new instance of the Expression class.
     * @param _category The expression's category.
     * @param _name The expression's friendly name.
     * @param _syntax The expression's syntax.
     * @param _description The expression's description.
     */
    constructor(private _category: ExpressionCategory, private readonly _name: string, private readonly _syntax: string, private readonly _description: string) {
    }

    /**
     * Gets the expression's friendly name.
     */
    public get name(): string {
        return this._name;
    }

    /**
     * Gets the expression's syntax.
     */
    public get syntax(): string {
        return this._syntax;
    }

    /**
     * Gets the expression's description.
     */
    public get description(): string {
        return this._description;
    }

    /**
     * Gets the expression's category.
     */
    public get category(): ExpressionCategory {
        return this._category;
    }

    /**
     * Compiles the expression information into the expected syntax.
     */
    public compile(): string {
        return this.syntax;
    }
}

/**
 * Defines an expression that returns a simple value of a specific type.
 */
export class ScalarExpression extends Expression {
    public constructor(
        category: ExpressionCategory,
        name: string,
        syntax: string,
        description: string,
        private readonly _returnType: ExpressionType,
        private readonly _subReturnType?: ExpressionType,
        private readonly _activityType: string = '') {
        super(category, name, syntax, description);
    }

    /**
     * Gets the expression's return type.
     */
    public get returnType(): ExpressionType {
        return this._returnType;
    }

    /**
     * Gets the expression's return sub type in case of arrays.
     */
    public get returnSubType(): ExpressionType {
        return this._subReturnType;
    }

    /**
     * Gets the activity type which defines this expression (useful for lookup activities)
     */
    public get activityType(): string {
        return this._activityType;
    }
}

/**
 * Defines an expression function's overload.
 */
export interface IFunctionOverload {
    /**
     * Function argument list.
     */
    args: Argument[];

    /**
     * Function return type.
     */
    returnType: ExpressionType;

    /**
     * Function return sub-type in case of arrays.
     */
    subReturnType?: ExpressionType;
}

/**
 * Defines an expression function specification.
 */
export class FunctionSpecification {
    private _specifications: IFunctionOverload[];

    constructor(...specifications: IFunctionOverload[]) {
        this._specifications = specifications || [];
    }

    /**
     * Indicates whether the function is overloaded or not.
     */
    public get isOverloaded(): boolean {
        return this._specifications.length > 1;
    }

    /**
     * Returns the arguments of the function's first overload.
     */
    public get args(): Argument[] {
        return this._specifications[0].args;
    }

    /**
     * Returns the return type of the function's first overload.
     */
    public get returnType(): ExpressionType {
        return this._specifications[0].returnType;
    }

    /**
     * Returns the sub-return type of the function's first overload.
     */
    public get subReturnType(): ExpressionType {
        return this._specifications[0].subReturnType;
    }

    /**
     * Returns the function's overloads.
     */
    public get overloads(): IFunctionOverload[] {
        return this._specifications;
    }
}

/**
 * Defines an expression function capable of accepting arguments and being overloaded.
 */
export class ExpressionFunction extends Expression {
    /**
     * Initializes a new instance of the ExpressionFunction class.
     * @param name The expression's friendly name.
     * @param syntax The expression's syntax.
     * @param description The expression's description.
     * @param _specification The function's specification.
     * @param _functionCategory The category used to group expression functions.
     */
    constructor(name: string, syntax: string, description: string, private _specification: FunctionSpecification, private _functionCategory: ExpressionFunctionCategory) {
        super(ExpressionCategory.Function, name, syntax, description);
    }

    /**
     * Gets the expression function's specification.
     */
    public get specification(): FunctionSpecification {
        return this._specification;
    }

    /**
     * Gets the expression function's sub category.
     */
    public get functionCategory(): ExpressionFunctionCategory {
        return this._functionCategory;
    }

    /**
     * Compiles the expression function information into the expected syntax.
     */
    public compile(): string {
        return this.syntax.concat('()');
    }

    /**
     * Returns the overloads that match the given argument count.
     */
    public matchArgumentCount(argCount: number): IFunctionOverload[] {
        const matchingOverloads: IFunctionOverload[] = [];

        for (const overload of this._specification.overloads) {
            const minAcceptedArgCount = overload.args.length - overload.args.filter((arg) => !arg.required).length;
            const maxAcceptedArgCount = overload.args.length > 0 && overload.args[overload.args.length - 1].variableLength ? Number.MAX_VALUE : overload.args.length;

            if (argCount >= minAcceptedArgCount && argCount <= maxAcceptedArgCount) {
                matchingOverloads.push(overload);
            }
        }

        return matchingOverloads;
    }

}

/**
 * Builds a system variable expression.
 * @param name The system variable name.
 * @param syntax The system variable syntax.
 * @param description Description of the system variable.
 */
export function systemVariableExpression(name: string, syntax: string, description: string): { returns: (returnType: ExpressionType) => ScalarExpression } {
    return {
        returns: (returnType: ExpressionType): ScalarExpression => {
            return new ScalarExpression(ExpressionCategory.SystemVariable, name, syntax, description, returnType);
        }
    };
}

/**
 * Builds a trigger parameter expression.
 * @param name The trigger parameter name.
 * @param syntax The trigger parameter syntax.
 * @param description Description of the trigger parameter.
 */
export function triggerParameterExpression(name: string, syntax: string, description: string): { returns: (returnType: ExpressionType) => ScalarExpression } {
    return {
        returns: (returnType: ExpressionType): ScalarExpression => {
            return new ScalarExpression(ExpressionCategory.TriggerParameter, name, syntax, description, returnType);
        }
    };
}

interface IExpressionFunctionReturnTypeBuilder {
    returns: (returnType: ExpressionType, subReturnType?: ExpressionType) => ExpressionFunction;
}

interface IExpressionFunctionOverloadBuilder {
    overload: (...args: Argument[]) => { returns: (returnType: ExpressionType, subReturnType?: ExpressionType) => ITerminalExpressionFunctionOverloadBuilder };
}

interface ITerminalExpressionFunctionOverloadBuilder extends IExpressionFunctionOverloadBuilder {
    build: () => ExpressionFunction;
}

/**
 * Builds an expression function that supports a single overload.
 * @param name The name of the function.
 * @param syntax The function syntax.
 * @param description The function description.
 * @param functionCategory The function category.
 */
export function functionExpression(
    name: string, syntax: string, description: string, functionCategory: ExpressionFunctionCategory): { args: (...args: Argument[]) => IExpressionFunctionReturnTypeBuilder } {
    return {
        args: (...args: Argument[]): IExpressionFunctionReturnTypeBuilder => {
            return {
                returns: (returnType: ExpressionType, subReturnType?: ExpressionType): ExpressionFunction => {
                    return new ExpressionFunction(name, syntax, description, new FunctionSpecification({ args, returnType, subReturnType }), functionCategory);
                }
            };
        }
    };
}

/**
 * Builds an overloaded expression function.
 * @param name The name of the function.
 * @param syntax The function syntax.
 * @param description The function description.
 * @param functionCategory The function category.
 */
export function overloadedFunctionExpression(name: string, syntax: string, description: string, subCategory: ExpressionFunctionCategory): ITerminalExpressionFunctionOverloadBuilder {
    const expressionFunction = new ExpressionFunction(name, syntax, description, new FunctionSpecification(), subCategory);

    const functionOverloadBuilder: ITerminalExpressionFunctionOverloadBuilder = {
        build: (): ExpressionFunction => expressionFunction,
        overload: (...args: Argument[]): { returns: (returnType: ExpressionType, subReturnType?: ExpressionType) => ITerminalExpressionFunctionOverloadBuilder } => {
            return {
                returns: (returnType: ExpressionType, subReturnType?: ExpressionType): ITerminalExpressionFunctionOverloadBuilder => {
                    expressionFunction.specification.overloads.push({ args, returnType, subReturnType });
                    return functionOverloadBuilder;
                }
            };
        }
    };

    return functionOverloadBuilder;
}
